/**
 * Phase 5b — Supervisor Tools.
 *
 * Single page with three sections:
 *   1) Agent picker with state + per-row action buttons
 *      (listen / whisper / barge / force-pause / force-logout)
 *   2) Broadcast composer (target=all or queue list, severity, optional title)
 *   3) Recent broadcasts the supervisor authored, with retire button
 *
 * Each action confirms first because they touch the agent immediately.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supervisorApi, queuesApi } from '../../../api/client';
import TenantPicker, { useTenantFilter } from './TenantPicker';
import HelpButton from '../../help/HelpButton';

const REFRESH_MS = 5000;

// State pill palette. Keep in sync with the backend enum values from
// agent_state_enum: logout, ready, aux, wrap, on_call, on_call_outbound.
// Any missing enum value would fall through to a neutral pill — which is
// the 2026-07-16 bug Fernando reported (on_call_outbound had no entry
// so the label showed raw enum string "on_call_outbound").
const STATE_PILL = {
  ready:            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  aux:              'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  wrap:             'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  on_call:          'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  on_call_outbound: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
  logout:           'bg-surface-subtle text-surface-muted dark:bg-surface-dark-subtle',
};
const STATE_LABEL = {
  ready:            'Ready',
  aux:              'Not Ready',    // "AUX" in call-center jargon = logged in but paused
  wrap:             'Wrap-up',
  on_call:          'On Call (in)',
  on_call_outbound: 'On Call (out)',
  logout:           'Logged Out',
};

export default function SupervisorTools() {
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <AgentsPanel />
      <BroadcastsPanel />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Agents panel
// ────────────────────────────────────────────────────────────────────

function AgentsPanel() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [clientId, setClientId] = useTenantFilter();
  const { data: agents = [], isLoading, error } = useQuery({
    queryKey: ['supervisor', 'agents', clientId || ''],
    queryFn: () => supervisorApi.agents(clientId || undefined),
    refetchInterval: REFRESH_MS,
  });

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const runAction = async (label, mutationFn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const r = await mutationFn();
      showToast(`${label} → ok`);
      qc.invalidateQueries({ queryKey: ['supervisor', 'agents'] });
      qc.invalidateQueries({ queryKey: ['wallboard', 'floor'] });
      qc.invalidateQueries({ queryKey: ['wallboard', 'queues'] });
      return r;
    } catch (e) {
      const msg = (typeof e?.detail === 'string' && e.detail)
        || (typeof e?.message === 'string' && e.message)
        || 'request failed';
      showToast(`${label} → ${msg}`, 'error');
    }
  };

  const filtered = useMemo(() => {
    if (!filter.trim()) return agents;
    const q = filter.toLowerCase();
    return agents.filter((a) =>
      (a.extension || '').includes(q) ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.queues || []).some((qq) => qq.includes(q))
    );
  }, [agents, filter]);

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Supervisor actions</h2>
          <p className="text-xs text-surface-muted">
            Live agent list refreshes every {REFRESH_MS / 1000}s. Actions are audited and announced to the agent via SSE.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TenantPicker value={clientId} onChange={setClientId} />
          <HelpButton moduleKey="acd_supervisor" />
          <input
            type="search"
            placeholder="Filter by ext / name / queue"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm w-64"
          />
        </div>
      </div>

      {toast && (
        <div
          className={
            'mb-3 px-3 py-2 rounded-thin text-sm ' +
            (toast.kind === 'error'
              ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200'
              : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200')
          }
        >
          {toast.msg}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : error ? (
        <div className="text-sm text-red-600">{String(error.detail || error.message)}</div>
      ) : (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Ext</th>
                <th className="px-3 py-2 text-left font-medium">Agent</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">Queues</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-surface-muted">
                    No matching agents.
                  </td>
                </tr>
              )}
              {filtered.map((a) => {
                // 2026-08-11: prefer effective_state (raw state + authoritative
                // BUSY overlay for outbound/conference calls). on_call_actual
                // marks rows where the overlay corrected a stale "Ready".
                const state = a.effective_state || a.state || 'logout';
                return (
                  <tr key={a.extension} className="border-t border-surface-border dark:border-surface-dark-border">
                    <td className="px-3 py-2 font-mono">{a.extension}</td>
                    <td className="px-3 py-2">{a.name || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={'px-2 py-0.5 rounded-thin text-xs ' + (STATE_PILL[state] || STATE_PILL.logout)}>
                        {STATE_LABEL[state] || state}
                      </span>
                      {a.on_call_actual && (
                        <span
                          className="ml-1 text-[10px] text-surface-muted align-middle"
                          title="Live call detected from Asterisk channels (in/out reflects the call's origin); ACD state itself still shows the agent as available"
                        >●&nbsp;live</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-surface-muted">
                      {(a.queues || []).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                      <ActionBtn label="🎧 Listen" tone="blue" onClick={() => {
                        navigate('/softphone');
                        runAction('Listen', () => supervisorApi.spy(a.extension));
                      }} />
                      <ActionBtn label="💬 Whisper" tone="indigo" onClick={() => {
                        navigate('/softphone');
                        runAction('Whisper', () => supervisorApi.whisper(a.extension));
                      }} />
                      <ActionBtn label="🤝 Barge" tone="amber" onClick={() => {
                        navigate('/softphone');
                        runAction('Barge', () => supervisorApi.barge(a.extension),
                          `Barge into ${a.extension}'s call? Both parties will hear you.`);
                      }} />
                      <ActionBtn label="⏸ Pause" tone="amber" onClick={() => {
                        const reason = window.prompt('Pause reason (optional):', 'force-paused-by-supervisor');
                        if (reason === null) return;
                        runAction('Force pause', () => supervisorApi.forcePause(a.extension, reason || null));
                      }} />
                      <ActionBtn label="🚪 Logout" tone="red" onClick={() => {
                        const reason = window.prompt('Logout reason (optional):', '');
                        if (reason === null) return;
                        runAction('Force logout', () => supervisorApi.forceLogout(a.extension, reason || null),
                          `Force ${a.extension} out of every queue?`);
                      }} />
                      {/* 2026-08-20: deterministic hard reset for a STUCK agent
                          (e.g. stuck "On Call" from an orphaned leg that plain
                          Force Logout can't clear). Kills the agent's own
                          lingering channels + clears devstate + dequeues +
                          logs out. */}
                      <ActionBtn label="🧹 Hard Reset" tone="red" onClick={() => {
                        const reason = window.prompt('Hard reset a STUCK agent (kills their lingering call legs, clears "On Call", logs out). Reason (optional):', 'stuck-agent');
                        if (reason === null) return;
                        runAction('Hard reset', () => supervisorApi.hardReset(a.extension, reason || null),
                          `HARD RESET ${a.extension}? This force-hangs-up their lingering call legs, clears their On-Call state, removes them from all queues, and logs them out. Use only when the agent is stuck.`);
                      }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function ActionBtn({ label, tone = 'blue', onClick }) {
  const tones = {
    blue:   'text-blue-700 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30',
    indigo: 'text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
    amber:  'text-amber-700 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30',
    red:    'text-red-700 dark:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/30',
  };
  return (
    <button
      onClick={onClick}
      className={'px-2 py-1 rounded-thin text-xs border border-surface-border dark:border-surface-dark-border ' + (tones[tone] || tones.blue)}
    >
      {label}
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
// Broadcasts panel
// ────────────────────────────────────────────────────────────────────

function BroadcastsPanel() {
  const qc = useQueryClient();
  const [target, setTarget]   = useState('all');
  const [queueIds, setQueueIds] = useState([]);
  const [severity, setSeverity] = useState('info');
  const [title, setTitle] = useState('');
  const [body, setBody]   = useState('');
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  const { data: queues = [] } = useQuery({
    queryKey: ['admin', 'queues'],
    queryFn: () => queuesApi.list(),
    staleTime: 60_000,
  });
  const { data: prior = [] } = useQuery({
    queryKey: ['supervisor', 'broadcasts'],
    queryFn: () => supervisorApi.broadcasts(),
  });

  const send = useMutation({
    mutationFn: () => supervisorApi.broadcast({
      target,
      queue_ids: target === 'queue' ? queueIds : [],
      severity,
      title: title.trim() || null,
      body: body.trim(),
    }),
    onSuccess: (b) => {
      setSent(b);
      setTitle(''); setBody(''); setQueueIds([]); setError(null);
      qc.invalidateQueries({ queryKey: ['supervisor', 'broadcasts'] });
    },
    onError: (e) => setError(e.detail || e.message),
  });

  const remove = useMutation({
    mutationFn: (id) => supervisorApi.delBroadcast(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['supervisor', 'broadcasts'] }),
  });

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Broadcast</h2>
        <p className="text-xs text-surface-muted">
          Sends a short notice via SSE to every targeted agent's softphone. Agents dismiss it; we remember each dismissal.
        </p>
      </div>

      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin p-4 space-y-3 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Labeled label="Target">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
            >
              <option value="all">All agents</option>
              <option value="queue">By queue…</option>
            </select>
          </Labeled>
          <Labeled label="Severity">
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </Labeled>
          <Labeled label="Title (optional)">
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin" />
          </Labeled>
        </div>

        {target === 'queue' && (
          <Labeled label="Queues">
            <div className="flex flex-wrap gap-2">
              {queues.map((q) => {
                const on = queueIds.includes(q.queue_id);
                return (
                  <button
                    key={q.queue_id}
                    type="button"
                    onClick={() =>
                      setQueueIds((cur) =>
                        on ? cur.filter((x) => x !== q.queue_id) : [...cur, q.queue_id]
                      )
                    }
                    className={
                      'px-2 py-1 rounded-thin text-xs border ' +
                      (on
                        ? 'bg-continuum-500 text-white border-continuum-500'
                        : 'border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')
                    }
                  >
                    {q.queue_id} · {q.name}
                  </button>
                );
              })}
            </div>
          </Labeled>
        )}

        <Labeled label="Message">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Short message that agents will see on their softphone."
            className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin font-mono text-sm"
          />
        </Labeled>

        {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        {sent && (
          <div className="text-emerald-700 dark:text-emerald-300 text-sm">
            ✓ Sent at {new Date(sent.created_at).toLocaleTimeString()}.
          </div>
        )}

        <div className="flex justify-end">
          <button
            disabled={!body.trim() || (target === 'queue' && queueIds.length === 0) || send.isPending}
            onClick={() => send.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {send.isPending ? 'Sending…' : 'Send broadcast'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Your recent broadcasts</h3>
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">When</th>
                <th className="px-3 py-2 text-left font-medium">Target</th>
                <th className="px-3 py-2 text-left font-medium">Severity</th>
                <th className="px-3 py-2 text-left font-medium">Message</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {prior.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-surface-muted">
                    No broadcasts yet.
                  </td>
                </tr>
              )}
              {prior.map((b) => (
                <tr key={b.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    {b.target === 'all'
                      ? <span>All</span>
                      : <span className="font-mono text-xs">{(b.queue_ids || []).join(', ')}</span>}
                  </td>
                  <td className="px-3 py-2 capitalize">{b.severity}</td>
                  <td className="px-3 py-2">
                    {b.title && <div className="font-medium">{b.title}</div>}
                    <div className="text-xs text-surface-muted line-clamp-2">{b.body}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => { if (window.confirm('Retire this broadcast?')) remove.mutate(b.id); }}
                      className="text-red-600 text-sm"
                    >
                      Retire
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}


function Labeled({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
